import { useCallback } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";

const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (isNaN(d)) return "N/A";
    return d.toLocaleDateString("en-GB");
};

export const UseGeneratePDF = () => {
    const generatePDF = useCallback((
        empUserDetails,
        personalDetails,
        familyListDetails,
        educationListDetails,
        experienceListDetails,
        clientDetails,
        userdesignation,
        userdocslist
    ) => {
        const doc = new jsPDF();
        const pageHeight = doc.internal.pageSize.height;
        const margin = 10;
        const lineHeight = 7;
        const tableMarginBottom = 10;

        const client = clientDetails?.clientMaster?.[0] || {};

        let currentY = margin;

        // Helper to check page overflow
        const checkPageOverflow = (spaceNeeded = 20) => {
            if (currentY + spaceNeeded > pageHeight - margin) {
                doc.addPage();
                currentY = margin;
            }
        };

        // Client Details
        doc.setFontSize(22);
        doc.text(client.clientName || "N/A", margin, currentY);
        currentY += 10;

        doc.setFontSize(12);
        const addressLines = doc.splitTextToSize(client.clientAddress || "N/A", 100);
        doc.text(addressLines, margin, currentY);
        currentY += addressLines.length * lineHeight + 5;

        // currentY += addressLines.length * lineHeight + 5;

        // Add "Joining Form Details" header
        checkPageOverflow(15);
        doc.setFontSize(16);
        doc.setFont(undefined, "bold");
        const pageWidth = doc.internal.pageSize.width;
        doc.text("Joining Form Details", pageWidth / 2, currentY, { align: "center" });
        currentY += 10; // add some spacing after header
        doc.setFont(undefined, "normal"); // reset font for next content

        // Personal Details
        doc.setFontSize(16);
        checkPageOverflow();
        doc.text("Personal Details", margin, currentY);
        currentY += 5;
        doc.setFontSize(12);

        doc.autoTable({
            startY: currentY,
            head: [["Label", "Value"]],
            body: [
                ["Name", `${empUserDetails.firstName || "N/A"} ${empUserDetails.lastName || "N/A"}`],
                ["Designation", userdesignation || "N/A"],
                ["DOB", formatDate(empUserDetails.dob)],
                ["Employee No.", empUserDetails.empId || "N/A"],
                ["Gender", personalDetails.gender || "N/A"],
                ["Permanent Address", personalDetails.permanetAddress || "N/A"],
                ["Present Address", personalDetails.currentAddress || "N/A"],
                ["Email", empUserDetails.personalEmail || "N/A"],
                ["PAN", empUserDetails.pan || "N/A"],
                ["Mobile", personalDetails.mobileNumber || "N/A"],
                ["Blood Group", personalDetails.bloodGroup || "N/A"],
                ["Aadhar", personalDetails.aadhaar || "N/A"],
                ["Marital Status", personalDetails.maritalStatus || "N/A"],
                ["UAN", personalDetails.uan || "N/A"],
                ["ESCI", personalDetails.esic || "N/A"],
                ["Bank Name", personalDetails.bankName || "N/A"],
                ["Bank Account", personalDetails.bankAccount || "N/A"],
                ["Branch", personalDetails.branch || "N/A"],
                ["IFSC", personalDetails.ifsc || "N/A"],
            ],
            theme: 'grid',
            styles: { fontSize: 10 },
            headStyles: { fillColor: [46, 112, 202], textColor: 255 },
            margin: { bottom: tableMarginBottom },
            didDrawPage: (data) => {
                currentY = data.cursor.y + 8;
            },
        });

        // Reusable function for tables with no data message
        const renderTableWithNoData = (title, head, data) => {
            checkPageOverflow();
            doc.setFontSize(16);
            doc.text(title, margin, currentY);
            currentY += 5;
            doc.setFontSize(12);

            const tableBody = data.length > 0
                ? data
                : [[{ content: "No data available", colSpan: head[0].length, styles: { halign: 'center', fontStyle: 'italic' } }]];

            doc.autoTable({
                startY: currentY,
                head: head,
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 10 },
                headStyles: { fillColor: [46, 112, 202], textColor: 255 },
                margin: { bottom: tableMarginBottom },
                didDrawPage: (data) => {
                    currentY = data.cursor.y + 8;
                },
            });
        };

        // Family Details
        renderTableWithNoData(
            "Family Details",
            [["Relationship", "Name", "DOB"]],
            familyListDetails.map(f => [f.relationship, f.name, formatDate(f.dob)])
        );

        // Education Details
        renderTableWithNoData(
            "Education Details",
            [["Qualification", "University", "School", "Specialization", "Year", "%"]],
            educationListDetails.map(edu => [
                edu.qualification, edu.university, edu.college, edu.specialization, edu.yop, edu.percentage
            ])
        );

        // Experience Details
        renderTableWithNoData(
            "Experience Details",
            [["Company", "Designation", "From", "To", "Reason of Leaving"]],
            experienceListDetails.map(exp => [exp.company, exp.designation, formatDate(exp.fromDate), formatDate(exp.toDate), exp.reason])
        );
        currentY += lineHeight;
        // Document List Section
        if (userdocslist && userdocslist.length > 0) {
            checkPageOverflow();
            doc.setFontSize(16);
            doc.text("Submitted Documents", margin, currentY);
            currentY += 6;
            doc.setFontSize(12);

            const indent = margin + 6; // space from left

            userdocslist.forEach((docItem, index) => {
                const number = `${index + 1}.`; // 1., 2., 3. ...
                const fileName = docItem.fileName || "Untitled Document";
                const link = docItem.fileUrl || docItem.url || "#";

                // Combine number + name
                const text = `${number} ${fileName}`;

                // Optional: make links blue and clickable
                // doc.setTextColor(0, 0, 255);
                doc.textWithLink(text, indent, currentY, { url: link });
                doc.setTextColor(0, 0, 0);

                currentY += 7; // spacing between lines
            });

            currentY += 10; // bottom spacing after section
        } else {
            checkPageOverflow();
            doc.setFontSize(12);
            doc.text("No Documents Available", margin, currentY);
            currentY += 8;
        }



        currentY += lineHeight;

        // Declaration & Signature
        checkPageOverflow(25);
        doc.setFontSize(12);
        const declarationText =
            "I hereby confirm that all the information provided above is true, complete, and correct " +
            "to the best of my knowledge and belief. I understand that any false information or " +
            "misrepresentation may lead to disqualification or termination of my employment.";

        // Split the text into wrapped lines
        const wrappedText = doc.splitTextToSize(declarationText, 180);

        // Add indentation only to the first line
        if (wrappedText.length > 0) {
            wrappedText[0] = "     " + wrappedText[0]; 
        }

        // Now print the wrapped text
        doc.text(wrappedText, margin, currentY);



        currentY += lineHeight + 15;
        doc.text("Date:", margin, currentY);
        doc.text("Place:", margin, currentY + 5);
        doc.text("Signature:", 150, currentY + 5);


        doc.save(empUserDetails.firstName ? `${empUserDetails.firstName}.pdf` : "profile.pdf");

    }, []);

    return { generatePDF };
};
